import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tray, CheckCircle, ArrowCounterClockwise, DiscordLogo, Globe, User, Clock, Image as ImageIcon } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { SaleSettings } from "@/components/SaleSettings";
import { RevenuePanel } from "@/components/RevenuePanel";
import { UsersPanel } from "@/components/UsersPanel";
import { AdminsPanel } from "@/components/AdminsPanel";
import { listReportsApi, getReportApi, setReportStatusApi } from "@/lib/api";

const FILTERS = [["", "All"], ["new", "Open"], ["resolved", "Resolved"]];
const when = (iso) => new Date(iso).toLocaleString();

function StatusPill({ status }) {
  return status === "resolved"
    ? <span className="rounded-full bg-[#DFF6E4] px-2 py-0.5 text-[10px] font-semibold text-[#0B6B2A]">Resolved</span>
    : <span className="rounded-full bg-[#FFF4E5] px-2 py-0.5 text-[10px] font-semibold text-[#C93400]">Open</span>;
}

function ReportDetail({ report, onToggle }) {
  if (!report) return <div className="flex h-full items-center justify-center text-sm text-mist" data-testid="report-detail-empty">Select a report to see the evidence</div>;
  const resolved = report.status === "resolved";
  return (
    <div className="space-y-5" data-testid="report-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <StatusPill status={report.status} />
          <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed text-ink" data-testid="report-description">{report.description}</p>
        </div>
        <button data-testid="report-toggle-status-button" onClick={onToggle} className={`flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold ${resolved ? "bg-black/5 text-ink hover:bg-black/10" : "bg-ink text-white hover:bg-black"}`}>
          {resolved ? <><ArrowCounterClockwise size={15} weight="bold" /> Reopen</> : <><CheckCircle size={15} weight="fill" /> Mark resolved</>}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 text-xs text-mist sm:grid-cols-2">
        <span className="flex items-center gap-1.5"><User size={13} /> {report.email || "Anonymous"}</span>
        <span className="flex items-center gap-1.5"><Clock size={13} /> {when(report.created_at)}</span>
        <span className="flex items-center gap-1.5 truncate sm:col-span-2"><Globe size={13} /> {/^https?:\/\//i.test(report.url || "") ? <a data-testid="report-url-link" href={report.url} target="_blank" rel="noopener noreferrer" className="truncate text-brand hover:underline">{report.url}</a> : <span className="truncate">{report.url || "—"}</span>}</span>
        <span className="truncate sm:col-span-2" title={report.user_agent}>{report.user_agent}</span>
        {report.resolved_by && <span className="sm:col-span-2">Resolved by {report.resolved_by} · {when(report.resolved_at)}</span>}
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold text-ink">Screenshot</p>
        {/^data:image\/(png|jpeg|webp);base64,/.test(report.screenshot || "") ? (
          <img data-testid="report-screenshot" src={report.screenshot} alt="Bug evidence" className="w-full rounded-2xl border border-black/5 shadow-sm" />
        ) : <p className="flex items-center gap-1.5 text-xs text-mist"><ImageIcon size={14} /> No screenshot captured</p>}
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold text-ink">Console & error logs</p>
        <pre data-testid="report-logs" className="max-h-64 overflow-auto rounded-2xl bg-[#1D1D1F] p-4 font-mono text-[11px] leading-relaxed text-[#E5E5EA]">{report.console_logs || "No logs captured"}</pre>
      </div>
    </div>
  );
}

export default function AdminInbox() {
  const [filter, setFilter] = useState("new");
  const [data, setData] = useState({ items: [], counts: {}, discord_configured: false });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("reports");

  const load = () => listReportsApi(filter).then(setData).catch(() => toast.error("Could not load reports"));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const open = async (id) => setSelected(await getReportApi(id));
  const toggle = async () => {
    const next = selected.status === "resolved" ? "new" : "resolved";
    await setReportStatusApi(selected.id, next);
    toast.success(next === "resolved" ? "Marked as resolved" : "Reopened");
    setSelected({ ...selected, status: next });
    load();
  };

  return (
    <div className="stp-bg">
      <AppHeader />
      <main className="mx-auto w-full max-w-6xl px-5 pb-24 pt-10 sm:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-3 font-display text-4xl font-semibold text-ink sm:text-5xl"><Tray size={40} weight="fill" className="text-brand" /> Admin</h1>
            <p className="mt-2 text-base text-mist">Run your sale, track revenue and review every bug report.</p>
          </div>
          <span data-testid="discord-status" className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${data.discord_configured ? "bg-[#E8EAFD] text-[#4752C4]" : "bg-black/5 text-mist"}`}>
            <DiscordLogo size={14} weight="fill" /> {data.discord_configured ? "Discord alerts on" : "Discord alerts off · add your webhook link to turn them on"}
          </span>
        </div>
        <SaleSettings />
        <div className="stp-noscroll mb-6 flex max-w-full overflow-x-auto rounded-full bg-black/5 p-1 sm:w-fit">
          {[["reports", "Reports"], ["revenue", "Revenue"], ["users", "Users"], ["admins", "Admins"]].map(([id, label]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`min-h-[40px] flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium sm:flex-none sm:px-5 ${tab === id ? "bg-white text-ink shadow-sm" : "text-mist"}`}>{label}</button>
          ))}
        </div>
        {tab === "revenue" ? <RevenuePanel /> : tab === "users" ? <UsersPanel /> : tab === "admins" ? <AdminsPanel /> : <>
        <div className="mb-5 inline-flex rounded-full bg-black/5 p-1">
          {FILTERS.map(([id, label]) => (
            <button key={id || "all"} data-testid={`report-filter-${id || "all"}`} onClick={() => { setFilter(id); setSelected(null); }} className={`rounded-full px-4 py-1.5 text-xs font-medium ${filter === id ? "bg-white text-ink shadow-sm" : "text-mist"}`}>
              {label}{data.counts?.[id || "all"] !== undefined ? ` · ${data.counts[id || "all"]}` : ""}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
          <div className="stp-card max-h-[70vh] overflow-y-auto rounded-3xl p-2" data-testid="report-list">
            {data.items.length === 0 ? <p className="p-8 text-center text-sm text-mist" data-testid="report-list-empty">Inbox zero. Nice.</p> : data.items.map((r) => (
              <button key={r.id} data-testid="report-row" onClick={() => open(r.id)} className={`w-full rounded-2xl p-4 text-left transition-colors ${selected?.id === r.id ? "bg-brand-soft" : "hover:bg-black/[0.03]"}`}>
                <div className="flex items-center justify-between gap-2"><StatusPill status={r.status} /><span className="text-[11px] text-mist">{when(r.created_at)}</span></div>
                <p className="mt-2 line-clamp-2 text-sm text-ink">{r.description}</p>
                <p className="mt-1 truncate text-[11px] text-mist">{r.email || "Anonymous"}</p>
              </button>
            ))}
          </div>
          <div className="stp-card min-h-[50vh] rounded-3xl p-6 sm:p-8"><ReportDetail report={selected} onToggle={toggle} /></div>
        </div>
        </>}
      </main>
    </div>
  );
}
